import React, {useEffect, useMemo, useState} from "react";
import {Button, Form} from "react-bootstrap";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useAuth from "../../../hooks/useAuth";
import {toastError, toastSuccess} from "../../../helpers/Toast";

const errorMessage = error => error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null) || "Operacja nie powiodła się.";
const fullName = employee => [employee?.firstName, employee?.lastName].filter(Boolean).join(" ").trim();

const employeeDepartmentIds = employee => {
  const ids = new Set();
  if (employee?.department?.id) ids.add(Number(employee.department.id));
  (employee?.employeePositions || []).forEach(item => {
    if (item?.active === false) return;
    const id = item?.position?.department?.id || item?.department?.id;
    if (id) ids.add(Number(id));
  });
  return [...ids];
};

const toggleId = (items, id) => items.includes(id) ? items.filter(item => item !== id) : [...items, id];

const TrainingTestAssignments = () => {
  const axiosPrivate = useAxiosPrivate();
  const {auth} = useAuth();
  const roles = Array.isArray(auth?.roles) ? auth.roles : String(auth?.roles || "").replace(/[\[\]\s]/g, "").split(",");
  const isManagerOnly = roles.includes("ROLE_MANAGER") && !roles.includes("ROLE_HR") && !roles.includes("ROLE_ADMIN");
  const [tests, setTests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [employeeIds, setEmployeeIds] = useState([]);
  const [departmentIds, setDepartmentIds] = useState([]);
  const [positionIds, setPositionIds] = useState([]);
  const [employeeQuery, setEmployeeQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const departmentUrl = isManagerOnly ? "/seasonal-availability/departments" : "/departments";
        const [testsResponse, employeesResponse, departmentsResponse, positionsResponse] = await Promise.all([
          axiosPrivate.get("/training-tests"),
          axiosPrivate.get("/employees"),
          axiosPrivate.get(departmentUrl),
          axiosPrivate.get("/positions")
        ]);
        setTests(testsResponse.data || []);
        setEmployees(employeesResponse.data || []);
        setDepartments(departmentsResponse.data || []);
        setPositions(positionsResponse.data || []);
      } catch (error) {
        toastError(errorMessage(error));
      }
    };
    load();
  }, [axiosPrivate, isManagerOnly]);

  const selectedTest = tests.find(test => String(test.id) === String(selectedTestId));
  const accessibleDepartmentIds = useMemo(() => new Set(departments.map(department => Number(department.id))), [departments]);

  const availableDepartments = useMemo(() => {
    if (!selectedTest?.departmentId) return departments;
    return departments.filter(department => Number(department.id) === Number(selectedTest.departmentId));
  }, [departments, selectedTest]);

  const filteredEmployees = useMemo(() => {
    const phrase = employeeQuery.trim().toLocaleLowerCase("pl");
    return [...employees]
      .filter(employee => {
        const employeeDepartments = employeeDepartmentIds(employee);
        if (isManagerOnly && !employeeDepartments.some(id => accessibleDepartmentIds.has(id))) return false;
        if (selectedTest?.departmentId && !employeeDepartments.includes(Number(selectedTest.departmentId))) return false;
        return !phrase || `${fullName(employee)} ${employee.login || ""}`.toLocaleLowerCase("pl").includes(phrase);
      })
      .sort((a, b) => `${a.lastName || ""} ${a.firstName || ""}`.localeCompare(`${b.lastName || ""} ${b.firstName || ""}`, "pl"));
  }, [employees, employeeQuery, isManagerOnly, accessibleDepartmentIds, selectedTest]);

  const availablePositions = useMemo(() => [...positions]
    .filter(position => {
      const positionDepartmentId = position.department?.id ? Number(position.department.id) : null;
      if (isManagerOnly && positionDepartmentId && !accessibleDepartmentIds.has(positionDepartmentId)) return false;
      if (selectedTest?.departmentId && positionDepartmentId !== Number(selectedTest.departmentId)) return false;
      if (!departmentIds.length) return true;
      return positionDepartmentId && departmentIds.includes(positionDepartmentId);
    })
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "pl")), [positions, departmentIds, isManagerOnly, accessibleDepartmentIds, selectedTest]);

  const loadAssignments = async testId => {
    if (!testId) {
      setAssignments([]);
      return;
    }
    try {
      const response = await axiosPrivate.get("/training-test-assignments", {params: {testId}});
      setAssignments(response.data || []);
    } catch (error) {
      toastError(errorMessage(error));
    }
  };

  const clearTargets = () => {
    setEmployeeIds([]);
    setDepartmentIds([]);
    setPositionIds([]);
  };

  useEffect(() => {
    clearTargets();
    loadAssignments(selectedTestId);
  }, [selectedTestId]);

  const assign = async () => {
    if (!selectedTestId) {
      toastError("Wybierz test.");
      return;
    }
    if (!employeeIds.length && !departmentIds.length && !positionIds.length) {
      toastError("Wybierz pracowników, działy albo stanowiska.");
      return;
    }
    setSaving(true);
    try {
      const response = await axiosPrivate.post("/training-test-assignments/batch", {
        testId: Number(selectedTestId),
        employeeIds,
        departmentIds,
        positionIds,
        dueDate: dueDate || null
      });
      toastSuccess(`Przypisano test do ${response.data?.length || 0} pracowników.`);
      clearTargets();
      await loadAssignments(selectedTestId);
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const setAssignmentActive = async (assignment, active) => {
    try {
      await axiosPrivate.put(`/training-test-assignments/${assignment.id}/active`, null, {params: {active}});
      toastSuccess(active ? "Przywrócono przypisanie testu." : "Wycofano test z profilu pracownika.");
      await loadAssignments(selectedTestId);
    } catch (error) {
      toastError(errorMessage(error));
    }
  };

  return (
    <div className="card shadow-sm p-3 mb-4 bg-white rounded-2 training-assignment-card">
      <div className="training-card-heading mb-3">
        <div>
          <h5 className="mb-1">Przypisywanie testów pracownikom</h5>
          <span>Wyślij test bezpośrednio na profil wybranych pracowników, całego działu albo osób na wybranych stanowiskach.</span>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <Form.Label>Test</Form.Label>
          <Form.Select value={selectedTestId} onChange={event => setSelectedTestId(event.target.value)}>
            <option value="">-- wybierz test --</option>
            {tests.filter(test => test.active).map(test => (
              <option key={test.id} value={test.id}>{test.title} · {test.trainingName}{test.departmentName ? ` · ${test.departmentName}` : " · globalny"}</option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-3">
          <Form.Label>Termin wykonania</Form.Label>
          <Form.Control type="date" value={dueDate} onChange={event => setDueDate(event.target.value)}/>
        </div>
        <div className="col-md-3 d-flex align-items-end">
          <Button className="w-100" onClick={assign} disabled={saving || !selectedTestId}>{saving ? "Przypisywanie..." : "Przypisz test"}</Button>
        </div>
      </div>

      {selectedTest && (
        <div className="alert alert-light border py-2 mb-3">
          <strong>{selectedTest.title}</strong> · próg {selectedTest.passingScorePercent}% · maks. {selectedTest.maxAttempts} podejścia · wersja {selectedTest.definitionVersion || 1}
        </div>
      )}

      <div className="row g-3 training-assignment-targets">
        <div className="col-lg-4">
          <div className="training-assignment-selector">
            <strong>Działy</strong>
            <small>Zaznaczenie działu przypisze test wszystkim jego pracownikom.</small>
            <div className="training-assignment-scroll">
              {availableDepartments.map(department => (
                <Form.Check key={department.id} type="checkbox" label={department.name} checked={departmentIds.includes(Number(department.id))} onChange={() => setDepartmentIds(prev => toggleId(prev, Number(department.id)))}/>
              ))}
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="training-assignment-selector">
            <strong>Stanowiska</strong>
            <small>Możesz dodatkowo wskazać pracowników według stanowiska.</small>
            <div className="training-assignment-scroll">
              {availablePositions.map(position => (
                <Form.Check key={position.id} type="checkbox" label={`${position.name}${position.department?.name ? ` · ${position.department.name}` : ""}`} checked={positionIds.includes(Number(position.id))} onChange={() => setPositionIds(prev => toggleId(prev, Number(position.id)))}/>
              ))}
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="training-assignment-selector">
            <strong>Wybrani pracownicy</strong>
            <Form.Control size="sm" className="my-2" placeholder="Szukaj pracownika" value={employeeQuery} onChange={event => setEmployeeQuery(event.target.value)}/>
            <div className="training-assignment-scroll">
              {filteredEmployees.map(employee => (
                <Form.Check key={employee.id} type="checkbox" label={fullName(employee)} checked={employeeIds.includes(Number(employee.id))} onChange={() => setEmployeeIds(prev => toggleId(prev, Number(employee.id)))}/>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <Button size="sm" variant="outline-secondary" onClick={clearTargets}>Wyczyść wybór</Button>
      </div>

      <hr/>
      <h6>Aktualne przypisania</h6>
      {!selectedTestId ? <div className="text-muted">Wybierz test, aby zobaczyć przypisania.</div> : assignments.length === 0 ? <div className="text-muted">Ten test nie został jeszcze przypisany bezpośrednio żadnemu pracownikowi.</div> : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle mb-0">
            <thead><tr><th>Pracownik</th><th>Dział</th><th>Termin</th><th>Podejścia</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {assignments.map(assignment => (
                <tr key={assignment.id}>
                  <td>{assignment.employeeFirstName} {assignment.employeeLastName}</td>
                  <td>{assignment.departmentNames || "-"}</td>
                  <td>{assignment.dueDate || "-"}</td>
                  <td>{assignment.attemptCount} / {assignment.maxAttempts} <span className="text-muted">(pozostało {assignment.remainingAttempts})</span></td>
                  <td><span className={assignment.active ? "text-success fw-bold" : "text-secondary"}>{assignment.active ? "AKTYWNE" : "WYCOFANE"}</span></td>
                  <td className="text-end">
                    <Button size="sm" variant={assignment.active ? "outline-danger" : "outline-success"} onClick={() => setAssignmentActive(assignment, !assignment.active)}>
                      {assignment.active ? "Wycofaj" : "Przywróć"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TrainingTestAssignments;
